import { useState, MouseEvent, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import Buttons, { Btn } from "../../../components/Buttons/Buttons";
import PopupHeader from "../../../components/Popups/PopupHeader";
import PopupSideNav from "../../../components/Popups/PopupSideNav";
import PopupFooter from "../../../components/Popups/PopupFooter";
import SetTrainingParameters from "./TrainModelComponents/SetTrainingParameters";
import { ACTrainModelDAGRequestModel, Dataset } from "../../../api/models";

import { ReactComponent as PlusIcon } from '../../../assets/Icons/adornments/Plus.svg';
import SuccessMessageModal from "../../../components/Popups/SuccessMessageModal";
import usePost from "../../../hooks/endpoints/usePost";
import ModalBase from "../../../components/Popups/ModalBase";
import SelectDatasetV2 from "../../Resultsets/components/RunPredictionComponents/SelectDatasetV2";
import { customInstance } from "../../../api/custom-instance";
import { getDefaultColumns } from "../../../utils/dataExtractionFns";
import { classificationMethods, regressionMethods } from "../../../utils/supplementalData";


const navItems = ['Select Dataset', 'Set Training Parameters'];

const dcmMethodOptions = [
    { value: 'RandomForestClassifier', label: 'RandomForestClassifier' },
    { value: 'LogisticRegression', label: 'LogisticRegression' },
    { value: 'XGBClassifier', label: 'XGBClassifier' },
];

const dcmSourceOptions = [
    { value: 'ChEMBL', label: 'ChEMBL' },
];

const defaultForm: ACTrainModelDAGRequestModel = {
    models: [],
    dataset: {
        dataset_hash: '',
        structure_col: '',
        y_col: '',
    },
    fp_type: ['ecfp6_1024'],
    name: '',
    tag_behavior: 'define',
    tags: [],
    sampling_method: '',
}

export default function TrainModelModal({
    selectedDatasetId,
    setPopupMessage,
    isDropdownItem,
    handleCloseMenu,
    open: openProp,
    setOpen: setOpenProp,
}: {
    selectedDatasetId?: string,
    setPopupMessage : (popupMessage: string, isSuccessMessage: boolean) => void,
    isDropdownItem: boolean,
    handleCloseMenu?: () => void,
    open?: boolean,
    setOpen?: Dispatch<SetStateAction<boolean>>,
}) {
    const [openBackup, setOpenBackup] = useState(false);
    // Use prop if provided, otherwise fallback to internal state
    const open = openProp !== undefined ? openProp : openBackup;
    const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenBackup;

    const handleOpen = () => {
        setOpen(true);
    };
    const [ showSelect, setShowSelect ] = useState(false);

    const [ selectedNavIndex, setSelectedNavIndex ] = useState(0);
    const [ selectedDatasets, setSelectedDatasets ] = useState<Dataset[]>([]);
    const [ form, setForm ] = useState<ACTrainModelDAGRequestModel>(defaultForm);

    const [ isDisabled, setIsDisabled ] = useState(true);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isPrefetching, setIsPrefetching ] = useState(false);
    const [ isSuccess, setIsSuccess ] = useState(false);
    const [ btns, setBtns ] = useState<Btn[]>([]);

    const [ selectedDSType, setSelectedDSType ] = useState<'continuous' | 'single-class' | 'multi-class' | null>(null);
    const [ modelOptions, setModelsOptions ] = useState<{ value: string, label: string }[]>([]);
    const [ samplingOptions, setSamplingOptions ] = useState<{ value: string, label: string }[]>([]);
    const [ isDcmMode, setIsDcmMode ] = useState(false);
    const [ selectedDcmMethod, setSelectedDcmMethod ] = useState<string>(dcmMethodOptions[0].value);
    const [ selectedDcmSource, setSelectedDcmSource ] = useState<string>(dcmSourceOptions[0].value);

    const handleClose = () => {
        setOpen(false);
        resetValues();
        setShowSelect(false);
    };

    const resetValues = () => {
        setSelectedNavIndex(0);
        setSelectedDatasets([]);
        setForm(defaultForm);
        setIsDisabled(false);
        setIsLoading(false);
        setIsSuccess(false);
        setIsDcmMode(false);
        setSelectedDcmMethod(dcmMethodOptions[0].value);
        setSelectedDcmSource(dcmSourceOptions[0].value);

        handleCloseMenu && setTimeout(() => handleCloseMenu(), 0);
    };

    const { handleTrainModel } = usePost({
        setIsDisabled,
        setIsLoading,
        setPopupMessage,
        setIsSuccess,
    })

    useEffect(() => {
        const fetchDataset = async (id: string) => {
            setIsPrefetching(true);
            try {
                const response = await customInstance({
                    url: `dataset/?dataset_hash=${id}&include_dataset_metadata=true&include_airflow_metadata=false`,
                    method: 'GET',
                });
                const data = response as unknown as Dataset;
                setSelectedDatasets([data]);
            } catch (err:any) {
                console.log(err);
            }
            setIsPrefetching(false);
        }

        if (selectedDatasetId) {
            setSelectedNavIndex(0);
            fetchDataset(selectedDatasetId);
        }
    }, [selectedDatasetId]);
 
    
    const fieldOptions: { value: string, label: string }[] = useMemo(() => {
        if (selectedDatasets.length === 0) return [];

        let options: { value: string, label: string }[] = [];

        const selectedDataset = selectedDatasets[0];

        const columnMetadata = selectedDataset.dataset_metadata!.column_metadata;
        if (columnMetadata) {
            options = Object.keys(columnMetadata).map(meta => ({ value: meta, label: meta }));
        };

        return options;
    }, [selectedDatasets]);


    useEffect(() => {
        if (!selectedDatasets.length) return;

        const allMethods = classificationMethods.concat(regressionMethods);
        let showSamplingMethods = false;
        const selectedDataset = selectedDatasets[0];
        // const fieldOptions = ... // your logic to compute fieldOptions
        const { structureCol, activityCol } = getDefaultColumns(selectedDataset.dataset_metadata);
        const tags: string[] = [];
        const dsTags = selectedDataset.file_metadata && selectedDataset.file_metadata.tags ? selectedDataset.file_metadata.tags as string[] : [];
            dsTags.forEach(tag => {
                const tagValue = tag ?? 'null';
                if (!tags.includes(tagValue)) {
                    tags.push(tagValue);
                }
            });


        // Compute dsType
        let dsType: 'continuous' | 'single-class' | 'multi-class' | null = null;
        const columnMetadata = selectedDataset.dataset_metadata?.column_metadata;
        if (activityCol && columnMetadata) {
            const meta = columnMetadata[activityCol];
            if (meta?.data_type?.includes('single')) dsType = 'single-class';
            else if (meta?.data_type?.includes('continuous')) dsType = 'continuous';
            else if (meta?.data_type?.includes('multi')) dsType = 'multi-class';
        }
        if (!dsType) {
            const datasetTypes = selectedDataset.dataset_metadata?.dataset_types || [];
            if (datasetTypes.includes('multi-class-label')) dsType = 'multi-class';
            else if (datasetTypes.includes('single-class-label')) dsType = 'single-class';
            else if (datasetTypes.includes('continuous-value')) dsType = 'continuous';
        }

        // Compute methods
        let methods: string[] = [];
        if (dsType === 'multi-class') methods = classificationMethods.concat(regressionMethods);
        else if (dsType === 'continuous') methods = regressionMethods;
        else if (dsType === 'single-class') methods = classificationMethods;

        if (isDcmMode) {
            methods = [selectedDcmMethod];
        }

        setModelsOptions(methods.map(method => ({ value: method, label: method })));
        setSelectedDSType(dsType);

        if (dsType) {
            if (dsType === 'multi-class') {
                methods = allMethods;
            } else if (dsType === 'continuous') {
                methods = regressionMethods;
            } else {
                methods = classificationMethods;
            }

            if (isDcmMode) {
                methods = [selectedDcmMethod];
            }

            setModelsOptions(methods.map(method => ({ value: method, label: method })));
            
        }

        setSelectedDSType(dsType);
        if (dsType === 'single-class') {
            showSamplingMethods = true;
        }


        setForm({
            ...defaultForm,
            name: selectedDataset.name ?? '',
            // tags: (selectedDataset.file_metadata?.tags ?? []).filter(Boolean),
            tags,
            dataset: {
                ...defaultForm.dataset,
                dataset_hash: selectedDataset.file_hash!,
                structure_col: structureCol || '',
                y_col: activityCol || '',
            },
            models: methods,
            sampling_method: showSamplingMethods && !isDcmMode ? '' : undefined,
        });
    }, [selectedDatasets, isDcmMode, selectedDcmMethod]);

    useEffect(() => {
        if (form.models!.length < 1 || !form.dataset!.structure_col || !form.dataset!.y_col || !form.dataset!.dataset_hash || form.fp_type!.length < 1 || !form.name || selectedDatasets.length === 0) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [form, selectedDatasets.length]);

    useEffect(() => {
        setBtns([
            {
                type: "tertiary",
                styling: "outline",
                id: 'cancel-model-training',
                label: 'Cancel',
                action: (e: MouseEvent<HTMLButtonElement>) => {handleClose()},
            },
            {
                type: isDisabled ? 'disabled' : 'primary',
                styling: "solid",
                id: 'train-model-btn',
                label: "Run",
                action: async (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleTrainModel(e, form, {
                        isDcmMode,
                        dcmMethod: selectedDcmMethod,
                        dcmUnifiedSourceName: selectedDcmSource,
                    });
                },
                isLoading,
            }
        ]);

        //eslint-disable-next-line
    }, [isDisabled, isLoading, form, isDcmMode, selectedDcmMethod, selectedDcmSource]);


    const checkIfLocked = (index: number) => {
        if (navItems[index] !== 'Select Dataset') {
            if (selectedDatasets.length > 0) {
                return false;
            } else {
                return true;
            }
        } else {
            return false;
        }
    }

    const checkIfComplete = (index: number) => {
        if (selectedDatasets.length > 0) {
            if (navItems[index] === 'Select Dataset') {
                return true;
            } else if (navItems[index] === 'Set Training Parameters') {
                if (form.name && form.models!.length > 0 && form.dataset!.structure_col && form.dataset!.y_col && form.fp_type!) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        } else {
            return false;
        }
    };
    
    return (
        <>
            {isDropdownItem ? (
                null
            ) : (
                <Buttons
                    id='train-model-btn'
                    label="Train Model"
                    type="primary"
                    styling="solid"
                    startingAdornment={<PlusIcon/>}
                    action={handleOpen}
                />
            )}
            <ModalBase
                open={open}
                setShowSelect={setShowSelect}
                modalId="train-model-popup"
            >
            {!isSuccess ? (
                <div className={`bg-white rounded-lg w-full max-w-[900px] popup-max-height mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'> 
                        <PopupHeader
                            label="Train Model"
                            hasOutline={true}
                            closeFn={handleClose}
                        />   
                        <div className="w-full flex justify-center items-stretch overflow-y-auto">
                            <PopupSideNav 
                                items={navItems.map((item, i) => ({
                                    label: item,
                                    type: 'number',
                                    isDisabled: checkIfLocked(i),
                                    isActive: selectedNavIndex === i ? true : false,
                                    isComplete: checkIfComplete(i),
                                    action: (e: MouseEvent<HTMLButtonElement>) => {setSelectedNavIndex(i)},
                                }))}
                                selectedIndex={selectedNavIndex}
                            />
                            <div className="w-full grow overflow-y-auto border-l border-outline">
                                {navItems.map((navItem, index) => (
                                    <div className={`w-full p-6 ${index === selectedNavIndex ? 'flex' : 'hidden'} flex-col gap-8 justify-start items-stretch min-h-[600px]`}>
                                        {navItem === 'Select Dataset' ? (
                                            <SelectDatasetV2 
                                                preselectedDatasetId={selectedDatasetId}
                                                selectedDatasets={selectedDatasets}
                                                setSelectedDatasets={setSelectedDatasets}
                                                isPrefetching={isPrefetching}
                                            />
                                        ) : (
                                            <SetTrainingParameters
                                                form={form}
                                                setForm={setForm}
                                                chosenDS={selectedDatasets.length > 0 ? selectedDatasets[0] : null}
                                                fieldOptions={fieldOptions}
                                                showSelect={showSelect}
                                                selectedDSType={selectedDSType}
                                                setSelectedDSType={setSelectedDSType}
                                                modelOptions={modelOptions}
                                                setModelsOptions={setModelsOptions}
                                                samplingOptions={samplingOptions}
                                                setSamplingOptions={setSamplingOptions}
                                                isDcmMode={isDcmMode}
                                                setIsDcmMode={setIsDcmMode}
                                                dcmMethodOptions={dcmMethodOptions}
                                                selectedDcmMethod={selectedDcmMethod}
                                                setSelectedDcmMethod={setSelectedDcmMethod}
                                                dcmSourceOptions={dcmSourceOptions}
                                                selectedDcmSource={selectedDcmSource}
                                                setSelectedDcmSource={setSelectedDcmSource}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <PopupFooter 
                            hasOutline={true}
                            buttons={btns}
                        />
                    </div>
                </div>
            ) : (
                <SuccessMessageModal 
                    handleClose={handleClose}
                    message="Your model is being trained. You can check its progress in the Jobs Queue. Once the job is complete, the results will appears in the Model Family page."
                />
            )}
            </ModalBase>
        </>
    )
}